import React, { useState, useMemo } from "react";
import useSettlementsByGroupId from "../hooks/useSettlementsByGroupId";
import useTotalEarnings from "../hooks/useTotalEarnings"; // Import the new hook
import SettlementModal from "../components/Settlement/SettlementModal";
import SettlementItem from "../components/Settlement/SettlementItem";
import { isTetra } from "../utils/access";

export default function Settlement() {
  const [showModal, setShowModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState("");
  
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [selectedMonth, setSelectedMonth] = useState((new Date().getMonth() + 1).toString());

  // Hook for the list of settlements
  const { settlements, isLoading: listLoading, error, message, refetch } = useSettlementsByGroupId(
    selectedYear, 
    selectedMonth
  );

  // Hook for the total money earned (using the month/year state)
const { totalEarnings, isLoading: earningsLoading } = useTotalEarnings(
  selectedMonth, 
  selectedYear
);

  const handleSubmitSuccess = () => {
    refetch();
  };

  const filteredSettlements = useMemo(() => {
    return selectedGroup
      ? settlements.filter(s => s.group_id === selectedGroup)
      : settlements;
  }, [settlements, selectedGroup]);

  const uniqueGroups = useMemo(() => {
    return [...new Set(settlements.map(s => s.group_id))];
  }, [settlements]);

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  const years = Array.from({ length: 5 }, (_, i) => (new Date().getFullYear() - i).toString());

  return (
    <div className="min-h-screen bg-neutral-50 p-6">
      <div className="max-w-6xl mx-auto">
        <header className="flex justify-between items-center mb-8">
          <h1 className="text-2xl font-semibold tracking-tight text-ink">Settlement Management</h1>
          {isTetra() && (
            <button
              onClick={() => setShowModal(true)}
              className="px-6 py-2.5 bg-neutral-900 text-white text-sm rounded hover:bg-neutral-800 transition-colors"
            >
              + ADD SETTLEMENT
            </button>
          )}
        </header>

        {/* Filter Section */}
        <div className="bg-white border rounded-lg p-6 mb-6 shadow-sm grid grid-cols-1 md:grid-cols-4 gap-6 items-end">
          {/* Group Filter */}
          <div>
            <label className="block text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-2">
              Group
            </label>
            <select
              value={selectedGroup}
              onChange={(e) => setSelectedGroup(e.target.value)}
              className="w-full px-4 py-2 border border-neutral-200 rounded text-sm outline-none focus:ring-1 focus:ring-neutral-400"
            >
              <option value="">All Groups</option>
              {uniqueGroups.map(id => <option key={id} value={id}>Group {id}</option>)}
            </select>
          </div>

          {/* Month Filter */}
          <div>
            <label className="block text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-2">
              Month
            </label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="w-full px-4 py-2 border border-neutral-200 rounded text-sm outline-none focus:ring-1 focus:ring-neutral-400"
            >
              {months.map((m, i) => (
                <option key={m} value={i + 1}>{m}</option>
              ))}
            </select>
          </div>

          {/* Year Filter */}
          <div>
            <label className="block text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-2">
              Year
            </label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              className="w-full px-4 py-2 border border-neutral-200 rounded text-sm outline-none focus:ring-1 focus:ring-neutral-400"
            >
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>

          {/* EARNINGS DISPLAY AREA */}
          <div className="bg-neutral-900 text-white p-4 rounded-lg shadow-inner text-center">
            <label className="block text-[10px] font-bold text-neutral-400 uppercase tracking-widest mb-1">
              Total Earnings
            </label>
            <div className="text-xl font-medium">
              {earningsLoading ? (
                <span className="opacity-50 animate-pulse">...</span>
              ) : (
                `${totalEarnings.toLocaleString(undefined, { minimumFractionDigits: 2 })}`
              )}
            </div>
          </div>
        </div>

        {/* Status Messages */}
        {error && <div className="bg-red-50 text-red-600 p-4 rounded-lg mb-6 border border-red-200 text-sm">{error}</div>}
        {message && <div className="bg-green-50 text-green-600 p-4 rounded-lg mb-6 border border-green-200 text-sm">{message}</div>}

        {/* Settlements List */}
        <div className="bg-white border rounded-lg shadow-sm overflow-hidden">
          {listLoading ? (
            <div className="p-20 text-center">
              <div className="inline-block animate-spin w-6 h-6 border-2 border-neutral-900 border-t-transparent rounded-full mb-2"></div>
              <p className="text-sm text-neutral-500">Updating settlements...</p>
            </div>
          ) : filteredSettlements.length === 0 ? (
            <div className="p-20 text-center text-neutral-500">
              <p>No settlements found for {months[parseInt(selectedMonth) - 1]} {selectedYear}.</p>
            </div>
          ) : (
            filteredSettlements.map((s, idx) => (
              <SettlementItem
                key={s.id} 
                settlement={s} 
                selectedMonth={selectedMonth}
                selectedYear={selectedYear}
                isLast={idx === filteredSettlements.length - 1} 
              />
            ))
          )}
        </div>

        <SettlementModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          onSubmitSuccess={handleSubmitSuccess}
        />
      </div>
    </div>
  );
}